/**
 * ROOM FINISHES (2026-10-11, docs/bim-plan/34-finish-studio.md §3 / step 6, Matt: wainscot,
 * backsplash and shower tile, "we're going to want to be able to apply wood paneling to walls and
 * ceilings too"). A room carries its own finish stack (RoomRecord.finishes):
 *  - WALL ZONES, bottom up: each runs from the one below to its `topFt` above the floor (null =
 *    to the ceiling), wearing a finish (or nothing = the wall as it is). Built on the room's side
 *    of every wall around it — only the length of wall that belongs to the room, with the doors
 *    and windows cut out (bim-geometry roomFinishes.ts);
 *  - the CEILING finish: laid under the room's ceiling like a painted ceiling finish (paneling
 *    and planks through the same layout engine as floors).
 */
import type { RoomFinishes, RoomFinishZone } from './types'

const IN = (n: number) => n / 12

/** zones in order with sane heights: strictly rising tops, the last one may be "to the ceiling" */
export function normalizeRoomZones(zones: RoomFinishZone[] | null | undefined): RoomFinishZone[] {
  if (!Array.isArray(zones)) return []
  const out: RoomFinishZone[] = []
  let prev = 0
  for (const z of zones) {
    if (!z) continue
    const top = z.topFt == null ? null : Number(z.topFt)
    if (top != null && (!Number.isFinite(top) || top <= prev + IN(0.25))) continue
    out.push({ topFt: top, materialId: z.materialId || null, ...(z.color ? { color: z.color } : {}) })
    if (top == null) break
    prev = top
  }
  return out
}

export function hasRoomFinishes(f: RoomFinishes | null | undefined): boolean {
  return !!f && (normalizeRoomZones(f.zones).some((z) => z.materialId) || !!f.ceiling)
}

/** starting points for the Room Finishes window (heights only; the finishes are picked there) */
export const ROOM_ZONE_PRESETS: { id: string; label: string; tops: (number | null)[] }[] = [
  { id: 'full', label: 'One finish, floor to ceiling', tops: [null] },
  { id: 'wainscot36', label: 'Wainscot to 36", finish above', tops: [3, null] },
  { id: 'wainscot48', label: 'Wainscot to 48", finish above', tops: [4, null] },
  { id: 'chair', label: 'Panel to 32", chair rail band, finish above', tops: [32 / 12, 36 / 12, null] },
  { id: 'tile8', label: "Tile to 8'-0\", finish above", tops: [8, null] },
]
